import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Divider,
  LinearProgress,
  Chip
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TimelineIcon from '@mui/icons-material/Timeline';
import RepeatIcon from '@mui/icons-material/Repeat';
import { useTranslation } from 'react-i18next';

/**
 * FrequencyMetrics Component
 * Displays detailed frequency metrics for a parameter in an expanded row
 *
 * @param {object} props
 * @param {object} props.data - Frequency data containing coverage, volume, and pattern info
 * @param {string} props.availableSince - Date when parameter became available
 * @param {string} props.sensitivity - Sensitivity level
 * @param {string} props.usagePattern - Usage pattern description
 */
const FrequencyMetrics = ({ data, availableSince, sensitivity, usagePattern }) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('pt')
    ? 'pt-PT'
    : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
      ? 'de-DE'
      : 'en-US';
  const caseCoverage = data?.caseCoverage;
  const totalCases = data?.totalCases;
  const casesWithParameter = data?.casesWithParameter;
  const stationCoverage = Array.isArray(data?.stationCoverage) ? data.stationCoverage : [];
  const avgMeasurementsPerCase = data?.avgMeasurementsPerCase;
  const medianMeasurementsPerCase = data?.medianMeasurementsPerCase;
  const totalMeasurements = data?.totalMeasurements;
  const measurementPattern =
    data?.measurementPattern && typeof data.measurementPattern === 'object'
      ? data.measurementPattern
      : {};
  const continuousCases = data?.continuousCases;

  const hasNumber = (value) => typeof value === 'number' && Number.isFinite(value);
  const formatNumber = (value) => (hasNumber(value) ? value.toLocaleString(locale) : '–');
  const formatMeasurementValue = (value) => (hasNumber(value) ? value : '–');

  /**
   * Group stations by category/area
   */
  const groupStationsByArea = (stations) => {
    const groups = {
      [t('dataCatalog.frequency.stationGroups.icu')]: { stations: [], totalPercentage: 0, color: '#1976d2' },
      [t('dataCatalog.frequency.stationGroups.imc')]: { stations: [], totalPercentage: 0, color: '#42a5f5' },
      [t('dataCatalog.frequency.stationGroups.ward')]: { stations: [], totalPercentage: 0, color: '#90caf9' },
      [t('dataCatalog.frequency.stationGroups.opDiagnostics')]: { stations: [], totalPercentage: 0, color: '#4caf50' },
      [t('dataCatalog.frequency.stationGroups.lab')]: { stations: [], totalPercentage: 0, color: '#ff9800' },
      [t('dataCatalog.frequency.stationGroups.other')]: { stations: [], totalPercentage: 0, color: '#9e9e9e' }
    };

    stations.forEach(station => {
      if (!station?.name) {
        return;
      }

      const stationPercentage = hasNumber(station.percentage) ? station.percentage : 0;

      if (station.name.includes('ICU') || station.name.includes('NICU') || station.name.includes('PICU')) {
        groups[t('dataCatalog.frequency.stationGroups.icu')].stations.push(station);
        groups[t('dataCatalog.frequency.stationGroups.icu')].totalPercentage += stationPercentage;
      } else if (station.name.includes('IMC')) {
        groups[t('dataCatalog.frequency.stationGroups.imc')].stations.push(station);
        groups[t('dataCatalog.frequency.stationGroups.imc')].totalPercentage += stationPercentage;
      } else if (station.name.includes('Station') || station.name.includes('Stroke Unit')) {
        groups[t('dataCatalog.frequency.stationGroups.ward')].stations.push(station);
        groups[t('dataCatalog.frequency.stationGroups.ward')].totalPercentage += stationPercentage;
      } else if (station.name.includes('OP') || station.name.includes('Anästhesie') || station.name.includes('Diagnostik')) {
        groups[t('dataCatalog.frequency.stationGroups.opDiagnostics')].stations.push(station);
        groups[t('dataCatalog.frequency.stationGroups.opDiagnostics')].totalPercentage += stationPercentage;
      } else if (station.name.includes('Labor')) {
        groups[t('dataCatalog.frequency.stationGroups.lab')].stations.push(station);
        groups[t('dataCatalog.frequency.stationGroups.lab')].totalPercentage += stationPercentage;
      } else {
        groups[t('dataCatalog.frequency.stationGroups.other')].stations.push(station);
        groups[t('dataCatalog.frequency.stationGroups.other')].totalPercentage += stationPercentage;
      }
    });

    // Calculate average percentage per area
    Object.keys(groups).forEach(key => {
      const group = groups[key];
      if (group.stations.length > 0) {
        group.avgPercentage = Math.round(group.totalPercentage / group.stations.length);
      } else {
        group.avgPercentage = 0;
      }
    });

    // Return as sorted array (by avgPercentage descending)
    return Object.entries(groups)
      .map(([name, data]) => ({ name, ...data }))
      .filter(group => group.stations.length > 0)
      .sort((a, b) => b.avgPercentage - a.avgPercentage);
  };

  const stationGroups = groupStationsByArea(stationCoverage);
  const continuousPercentage =
    hasNumber(continuousCases) && hasNumber(casesWithParameter) && casesWithParameter > 0
      ? `${((continuousCases / casesWithParameter) * 100).toFixed(0)}%`
      : '–';
  const measurementPatternTypical = measurementPattern?.typical || t('dataCatalog.frequency.noData');
  const hasStationData = stationGroups.length > 0;
  const hasCaseData = hasNumber(caseCoverage) || hasNumber(totalCases) || hasNumber(casesWithParameter);
  const hasVolumeData =
    hasNumber(avgMeasurementsPerCase) ||
    hasNumber(medianMeasurementsPerCase) ||
    hasNumber(totalMeasurements);

  return (
    <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
      <Grid container spacing={3}>
        {/* 1. Coverage / Verbreitung */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #1976d2',
            pl: 2,
            backgroundColor: '#f5f9ff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOnIcon sx={{ color: '#1976d2' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1976d2' }}>
                {t('dataCatalog.frequency.coverage')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {hasStationData
                  ? t('dataCatalog.frequency.stationAreas', { count: stationGroups.length })
                  : t('dataCatalog.frequency.noStationData')}
              </Typography>
              {hasStationData ? (
                <>
                  {stationGroups.slice(0, 3).map((group, idx) => (
                    <Box key={idx} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="caption" fontWeight={500}>
                          {group.name} ({group.stations.length})
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {group.avgPercentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={group.avgPercentage}
                        sx={{
                          height: 6,
                          borderRadius: 1,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: group.color
                          }
                        }}
                      />
                    </Box>
                  ))}
                  {stationGroups.length > 3 && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      {t('dataCatalog.frequency.moreAreas', { count: stationGroups.length - 3 })}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {t('dataCatalog.frequency.noDataAvailable')}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.caseCoverage')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {hasNumber(caseCoverage) ? t('dataCatalog.frequency.allCases', { value: caseCoverage }) : '–'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {hasCaseData
                  ? t('dataCatalog.frequency.casesOfTotal', {
                      cases: formatNumber(casesWithParameter),
                      total: formatNumber(totalCases),
                    })
                  : t('dataCatalog.frequency.noCaseData')}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* 2. Volume / Messumfang */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #2e7d32',
            pl: 2,
            backgroundColor: '#f5fef5'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TimelineIcon sx={{ color: '#2e7d32' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                {t('dataCatalog.frequency.volume')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.averagePerCase')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {hasNumber(avgMeasurementsPerCase)
                  ? t('dataCatalog.frequency.measurementsAverage', { value: formatNumber(avgMeasurementsPerCase) })
                  : '–'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {hasVolumeData
                  ? t('dataCatalog.frequency.median', { value: formatMeasurementValue(medianMeasurementsPerCase) })
                  : t('dataCatalog.frequency.noMeasurementData')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.total')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(totalMeasurements)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {hasVolumeData
                  ? t('dataCatalog.frequency.measurementsLastYear')
                  : t('dataCatalog.frequency.noMeasurementData')}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* 3. Pattern / Messmuster */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #ed6c02',
            pl: 2,
            backgroundColor: '#fff8f5'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RepeatIcon sx={{ color: '#ed6c02' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ed6c02' }}>
                {t('dataCatalog.frequency.pattern')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.typicalFrequency')}
              </Typography>
              <Chip
                label={measurementPatternTypical}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mb: 0.5 }}
              />
              <Typography variant="caption" display="block" color="textSecondary">
                {t('dataCatalog.frequency.interval', { value: measurementPattern.avgInterval || '–' })}
              </Typography>
              <Typography variant="caption" display="block" color="textSecondary">
                σ {measurementPattern.stdDev || '–'}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.continuousMeasurements')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {continuousPercentage}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {hasNumber(continuousCases)
                  ? t('dataCatalog.frequency.continuousCases', { count: formatNumber(continuousCases) })
                  : t('dataCatalog.frequency.noData')}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

    </Box>
  );
};

export default FrequencyMetrics;
